import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { Trophy } from 'phosphor-react';
import PodiumPosition from './PodiumPosition';
import { useLocation } from 'react-router-dom';

import '../styles/Podium.css';

const PodiumAnimation = () => {
  const location = useLocation();
  const [showConfetti, setShowConfetti] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [showPodium, setShowPodium] = useState(false);
  const [topPlayers, setTopPlayers] = useState([]);
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);
  const [isStarted, setIsStarted] = useState(false);

  // Use useRef instead of useState for audio objects
  const countdownSound = useRef(null);
  const victorySound = useRef(null);
  const previousCountdown = useRef(null);
  const hasPlayedInitialSound = useRef(false);

  useEffect(() => {
    // Initialize audio objects
    countdownSound.current = new Audio(process.env.PUBLIC_URL + '/sounds/countdown.mp3');
    victorySound.current = new Audio(process.env.PUBLIC_URL + '/sounds/victory.mp3');
    
    const loadAudio = async () => {
      try {
        await Promise.all([
          countdownSound.current.load(),
          victorySound.current.load()
        ]);
        setIsAudioLoaded(true);
      } catch (error) {
        console.error('Error loading audio:', error);
      }
    };
    loadAudio();

    // Cleanup
    return () => {
      countdownSound.current?.pause();
      victorySound.current?.pause();
    };
  }, []);

  const playCountdownSound = useCallback(() => {
    if (isAudioLoaded && countdownSound.current) {
      countdownSound.current.currentTime = 0;
      countdownSound.current.play().catch(console.error);
    }
  }, [isAudioLoaded]);

  const playVictorySound = useCallback(() => {
    if (isAudioLoaded) {
      victorySound.current.currentTime = 0;
      victorySound.current.play().catch(console.error);
    }
  }, [isAudioLoaded]);

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/ranking`);
        setTopPlayers(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching ranking:', error);
      }
    };

    fetchRanking();
  }, []);

  useEffect(() => {
    if (!isStarted) return;

    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownInterval);
          setShowPodium(true);
          setShowConfetti(true);
          playVictorySound();
          setTimeout(() => setShowConfetti(false), 5000);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [playVictorySound, isStarted]);

  useEffect(() => {
    if (location.state?.autoStart) {
      setIsStarted(true);
    }
  }, [location]);

  const getHeight = (position) => {
    switch (position) {
      case 1: return 'h-[55vh]';
      case 2: return 'h-[45vh]';
      case 3: return 'h-[35vh]';
      default: return 'h-[35vh]';
    }
  };

  const podiumVariants = {
    hidden: { opacity: 0, y: 100, scale: 0.8 },
    visible: { opacity: 1, y: 0, scale: 1 }
  };

  const startAnimation = () => {
    setIsStarted(true);
    if (isAudioLoaded) {
      playCountdownSound();
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--tw-gradient-stops))] from-blue-900/20 via-transparent to-transparent"></div>
      </div>

      <AnimatePresence>
        {!isStarted && (
          <div className="absolute inset-0 flex items-center justify-center">
            <button
              onClick={startAnimation}
              className="px-8 py-4 bg-blue-600 text-white text-xl font-bold rounded-lg shadow-lg 
                hover:bg-blue-700 transform hover:scale-105 transition-all duration-300"
            >
              Rozpocznij ceremonię 🎉
            </button>
          </div>
        )}

        {isStarted && !showPodium && countdown > 0 && (
          <motion.div 
            className="absolute inset-0 flex items-center justify-center"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 2, opacity: 0 }}
            key={countdown}
          >
            <div className="text-9xl font-black text-blue-400 drop-shadow-[0_0_30px_rgba(59,130,246,0.3)]">
              {countdown}
            </div>
          </motion.div>
        )}

        {showPodium && (
          <motion.div 
            className="relative flex flex-col h-screen"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="p-8">
              <h1 className="text-3xl font-bold text-white mb-2">Zwycięzcy</h1>
              <p className="text-gray-400">Gratulacje dla zwycięskich graczy!</p>
            </div>

            {showConfetti && (
              <Confetti 
                width={window.innerWidth} 
                height={window.innerHeight} 
                recycle={false} 
                numberOfPieces={200}
                colors={['#3B82F6', '#1D4ED8', '#2563EB', '#444']}
              />
            )}

            <div className="flex-1 flex justify-center items-center px-8">
              <div className="flex justify-center items-end space-x-8 h-[50vh] w-full max-w-5xl">
                {[2, 1, 3].map((position, index) => (
                  <motion.div
                    key={position}
                    className="flex-1"
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      delay: index * 0.2,
                      type: "spring",
                      stiffness: 100,
                      damping: 15
                    }}
                  >
                    <PodiumPosition
                      position={position}
                      player={topPlayers[position - 1]}
                      height={getHeight(position)}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PodiumAnimation;
